import React, { useState, useEffect, useMemo } from 'react';
import Select from 'react-select';
import '../style/MenuEditor.css';
import { host } from '../script/variables';
import uploadViaPresign from '../script/uploadViaPresignV2';
import MenuScan from './MenuScan';

const ACCEPTED_IMAGE_TYPES = ['image/jpeg', 'image/png', 'image/webp', 'image/heic'];

/* ═══════════════════════════════════════════════════════════
   Constants
═══════════════════════════════════════════════════════════ */
const MEAL_TYPES = ['breakfast', 'lunch', 'dinner', 'snacks', 'dessert', 'water', 'regular'];
const SIZES      = ['small', 'medium', 'large'];

const EMPTY_NEW_DISH = {
  name:        '',
  type:        'veg',
  mealType:    'regular',
  image:       '',
  isTaxable:   true,
  isAvailable: true,
  small:       { price: '', discount: '0' },
  medium:      { price: '', discount: '0' },
  large:       { price: '', discount: '0' },
};

const EMPTY_SIZE_ENABLED = { small: false, medium: false, large: false };

/* ─────────────────────────────────────────────────────────────
   Image upload constraints
   ─────────────────────────────────────────────────────────────
   Menu photos only need to be sharp enough to look good in a card
   ~200-400px wide, so we cap the long edge and re-encode everything
   as JPEG before it ever reaches S3. PNGs in particular are lossless
   and routinely 3-5x larger than a visually-equivalent JPEG, so this
   alone meaningfully cuts storage cost and speeds up menu loads for
   customers on slow connections.
───────────────────────────────────────────────────────────── */
/* ═══════════════════════════════════════════════════════════
   Helpers
═══════════════════════════════════════════════════════════ */
const flatDishToForm = (dish) => ({
  _id:         dish._id,
  name:        dish.name        ?? '',
  type:        dish.type        ?? 'veg',
  category:    dish.category    ?? '',
  mealType:    dish.mealType    ?? '',
  image:       dish.image       ?? '',
  isAvailable: dish.isAvailable ?? true,
  isTaxable:   dish.isTaxable   ?? true,
  small:  { price: dish.small?.price  ?? dish.sizes?.small?.price  ?? 0,
            discount: dish.small?.discount ?? dish.sizes?.small?.discount ?? 0 },
  medium: { price: dish.medium?.price ?? dish.sizes?.medium?.price ?? 0,
            discount: dish.medium?.discount ?? dish.sizes?.medium?.discount ?? 0 },
  large:  { price: dish.large?.price  ?? dish.sizes?.large?.price  ?? 0,
            discount: dish.large?.discount ?? dish.sizes?.large?.discount ?? 0 },
});

const formDishToPayload = (formDish, cafeId, enabledSizes) => {
  const sizes = {};
  for (const s of SIZES) {
    const include = enabledSizes ? enabledSizes[s] : true;
    if (include) {
      sizes[s] = {
        price:    Number(formDish[s]?.price)    || 0,
        discount: Number(formDish[s]?.discount) || 0,
      };
    }
  }
  return {
    _id:         formDish._id,
    cafeId,
    name:        formDish.name.trim(),
    type:        formDish.type,
    category:    formDish.category.trim(),
    mealType:    formDish.mealType,
    image:       formDish.image || null,
    isAvailable: formDish.isAvailable,
    isTaxable:   formDish.isTaxable,
    sizes,
  };
};


/* ═══════════════════════════════════════════════════════════
   CategoryCombo
   ─────────────────────────────────────────────────────────
   Props:
     existingCategories  string[]   — unique sorted list to show in dropdown
     value               string     — currently selected/typed category
     onChange            (str)=>void — called with the resolved category string
                                       or '' when cleared
═══════════════════════════════════════════════════════════ */
const CategoryCombo = ({ existingCategories, value, onChange }) => {
  /*
   * Internal mode:
   *   'existing' — user picked from the list
   *   '__new__'  — user is typing a brand-new category
   *
   * We derive initial mode from whether `value` already exists in the list.
   */
  const [mode, setMode] = useState(() =>
    value && !existingCategories.includes(value) ? '__new__' : 'existing'
  );
  const [newInput, setNewInput] = useState(
    value && !existingCategories.includes(value) ? value : ''
  );

  /* When the parent resets value to '' (e.g. after save), sync internal state */
  useEffect(() => {
    if (value === '') {
      setMode('existing');
      setNewInput('');
    }
  }, [value]);

  const handleSelectChange = (e) => {
    if (e.target.value === '__new__') {
      setMode('__new__');
      setNewInput('');
      onChange('');
    } else {
      setMode('existing');
      setNewInput('');
      onChange(e.target.value);
    }
  };

  const handleNewInput = (e) => {
    setNewInput(e.target.value);
    onChange(e.target.value);
  };

  const cancelNew = () => {
    setMode('existing');
    setNewInput('');
    const fallback = existingCategories[0] ?? '';
    onChange(fallback);
  };

  const selectValue = mode === '__new__' ? '__new__' : value;

  return (
    <div className="form-group">
      <label>Category</label>
      <select className="category-select" value={selectValue} onChange={handleSelectChange}>
        {/* Placeholder shown only when nothing is selected yet */}
        {!value && mode !== '__new__' && (
          <option value="" disabled>— select a category —</option>
        )}
        {existingCategories.map((cat) => (
          <option key={cat} value={cat}>{cat}</option>
        ))}
        <option value="__new__">＋ Add new category…</option>
      </select>

      {mode === '__new__' && (
        <div className="new-category-wrap">
          <input
            type="text"
            className="new-category-input"
            value={newInput}
            onChange={handleNewInput}
            placeholder="Type new category name…"
            autoFocus
          />
          {existingCategories.length > 0 && (
            <button type="button" className="cancel-new-cat-btn" onClick={cancelNew}>
              ← Use existing
            </button>
          )}
        </div>
      )}

      {value && (
        <p className="category-resolved-hint">
          Category: <strong>{value}</strong>
        </p>
      )}
    </div>
  );
};

/* ═══════════════════════════════════════════════════════════
   TaxableToggle
═══════════════════════════════════════════════════════════ */
const TaxableToggle = ({ value, onChange }) => (
  <div className="form-group taxable-group">
    <label>GST / Tax</label>
    <div className="taxable-toggle-wrap">
      <button
        type="button"
        className={`taxable-btn ${value ? 'taxable-on' : ''}`}
        onClick={() => onChange(true)}
      >
        ✓ Taxable
      </button>
      <button
        type="button"
        className={`taxable-btn ${!value ? 'taxable-off-on' : ''}`}
        onClick={() => onChange(false)}
      >
        ✕ MRP / No Tax
      </button>
    </div>
    <p className="taxable-hint">
      {value
        ? "GST will be calculated and added to this item's price."
        : 'Price is MRP inclusive — no additional tax will be applied.'}
    </p>
  </div>
);

/* ═══════════════════════════════════════════════════════════
   MenuEditor
═══════════════════════════════════════════════════════════ */
const MenuEditor = ({ cafe, setCafe }) => {
  const [menu, setMenu] = useState(cafe.menu ?? []);

  const [mode, setMode] = useState('edit'); // 'edit' | 'add' | 'scan'

  /* ── edit state ── */
  const [options,           setOptions]           = useState([]);
  const [selectedDish,      setSelectedDish]      = useState(null);
  const [selectedDishIndex, setSelectedDishIndex] = useState({
    mealTypeIndex: null, categoryIndex: null, dishIndex: null,
  });

  /* ── add state ── */
  const [newDish,          setNewDish]          = useState(EMPTY_NEW_DISH);
  const [selectedMealType, setSelectedMealType] = useState('regular');
  const [addCategory,      setAddCategory]      = useState('');
  const [sizeEnabled,      setSizeEnabled]      = useState(EMPTY_SIZE_ENABLED);

  /* ── shared ── */
  const [imageFiles,   setImageFiles]   = useState([]);
  const [imageUrls,    setImageUrls]    = useState([]);
  const [isSaving,     setIsSaving]     = useState(false);
  const [isUploading,  setIsUploading]  = useState(false);
  const [uploadError,  setUploadError]  = useState('');

  /* ─────────────────────────────────────────────────────────
     ALL unique categories across every meal type in the menu.
     Sorted alphabetically, deduped.
  ───────────────────────────────────────────────────────── */
  const allCategories = useMemo(() => {
    const set = new Set();
    menu.forEach((mealType) =>
      mealType.categories?.forEach((cat) => {
        if (cat.name?.trim()) set.add(cat.name.trim());
      })
    );
    return [...set].sort((a, b) => a.localeCompare(b));
  }, [menu]);

  /* ─────────────── effects ─────────────── */

  /* Rebuild Select options when menu changes */
  useEffect(() => {
    const opts = [];
    menu.forEach((mealType, mti) => {
      mealType.categories?.forEach((category, ci) => {
        category.dishes?.forEach((dish, di) => {
          opts.push({
            value: `${mti}-${ci}-${di}`,
            label: `${mealType.type.toUpperCase()} › ${category.name} › ${dish.name} (${dish.type})`,
          });
        });
      });
    });
    setOptions(opts);
  }, [menu]);

  /* ─────────────── image upload (S3) ───────────────
     Every accepted file (PNG or JPEG) is normalized to a
     resized, compressed JPEG blob before it's sent to S3 —
     PNGs are lossless and typically 3-5x larger than an
     equivalent-quality JPEG, so converting on the way in keeps
     storage cost down and menu pages loading fast for customers.
  ─────────────────────────────────────────────────────── */
  const handleImageUpload = async (files) => {
    try {
      const urls = await Promise.all(files.map((file) => uploadViaPresign(file, 'dish')));
      setImageUrls(urls);
      return urls;
    } catch (err) {
      console.error('Image upload failed:', err);
      setUploadError(err.message || 'Image upload failed. Please upload a PNG or JPG image.');
      return [];
    }
  };

  const handleFileChange = async (event) => {
    const files = Array.from(event.target.files);
    setUploadError('');
    const invalid = files.find((f) => !ACCEPTED_IMAGE_TYPES.includes(f.type));
    if (invalid) {
      setUploadError(`"${invalid.name}" isn't a PNG or JPG image. Please upload a PNG or JPG file.`);
      event.target.value = '';
      return;
    }

    setImageFiles(files);
    setIsUploading(true);
    const urls = await handleImageUpload(files);
    setIsUploading(false);
    if (!urls.length) return;

    const joined = urls.join(',');
    if (mode === 'add') {
      setNewDish((d) => ({ ...d, image: joined }));
    } else if (selectedDish) {
      setSelectedDish((d) => ({ ...d, image: joined }));
    }
  };

  /* ─────────────── select dish (edit mode) ─────────────── */
 const handleDishSelect = (option) => {
  if (!option) {
    setSelectedDish(null);
    setSelectedDishIndex({ mealTypeIndex: null, categoryIndex: null, dishIndex: null });
    return;
  }
  const [mti, ci, di] = option.value.split('-').map(Number);
  const dish = menu[mti].categories[ci].dishes[di];
  const categoryName = menu[mti].categories[ci].name; // ← the actual source of truth for category

  setSelectedDish(flatDishToForm({ ...dish, category: categoryName }));
  setSelectedDishIndex({ mealTypeIndex: mti, categoryIndex: ci, dishIndex: di });
};

  /* ─────────────── input change ─────────────── */
  const handleInputChange = (field, size, value) => {
    if (mode === 'edit') {
      if (!selectedDish) return;
      setSelectedDish((d) => {
        if (size) return { ...d, [size]: { ...d[size], [field]: value } };
        return { ...d, [field]: value };
      });
    } else {
      setNewDish((d) => {
        if (size) return { ...d, [size]: { ...d[size], [field]: value } };
        return { ...d, [field]: value };
      });
    }
  };

  /* ─────────────── API calls ─────────────── */
  const callApi = async (endpoint, body) => {
    const token = localStorage.getItem('partnertoken');
    const res   = await fetch(`${host}${endpoint}`, {
      method:  'POST',
      headers: { 'Content-Type': 'application/json', Authorization: token },
      body:    JSON.stringify(body),
    });
    const data = await res.json();
    if (!res.ok) throw new Error(data.msg || `HTTP ${res.status}`);
    return data;
  };

  /* ─────────────── update dish ─────────────── */
  const updateDish = async () => {
    if (!selectedDish) return;
    if (!selectedDish.category?.trim()) {
      alert('Please select or type a category');
      return;
    }
    setIsSaving(true);
    try {
      const payload = formDishToPayload(selectedDish, cafe._id, null);
      await callApi('/cafe/updatedish', { dish: payload });

      const { mealTypeIndex: mti, categoryIndex: ci, dishIndex: di } = selectedDishIndex;
      setMenu((prev) => {
        const next = structuredClone(prev);
        const existing = next[mti].categories[ci].dishes[di];
        next[mti].categories[ci].dishes[di] = {
          ...existing,
          name:        selectedDish.name,
          type:        selectedDish.type,
          category:    selectedDish.category,
          mealType:    selectedDish.mealType,
          isAvailable: selectedDish.isAvailable,
          isTaxable:   selectedDish.isTaxable,
          image:       selectedDish.image,
          small:       { price: Number(selectedDish.small.price),  discount: Number(selectedDish.small.discount) },
          medium:      { price: Number(selectedDish.medium.price), discount: Number(selectedDish.medium.discount) },
          large:       { price: Number(selectedDish.large.price),  discount: Number(selectedDish.large.discount) },
        };
        return next;
      });

      alert('Dish updated successfully!');
    } catch (err) {
      console.error('updateDish error:', err);
      alert('Error updating dish: ' + err.message);
    } finally {
      setIsSaving(false);
    }
  };

  /* ─────────────── delete dish ─────────────── */
  const deleteDish = async () => {
    if (!selectedDish) { alert('Please select a dish to delete'); return; }
    if (!window.confirm(`Delete "${selectedDish.name}"? This cannot be undone.`)) return;

    setIsSaving(true);
    try {
      await callApi('/cafe/updatedish', { dish: { _id: selectedDish._id, isRemoved: true } });

      const { mealTypeIndex: mti, categoryIndex: ci, dishIndex: di } = selectedDishIndex;
      setMenu((prev) => {
        const next = structuredClone(prev);
        next[mti].categories[ci].dishes.splice(di, 1);
        return next;
      });

      setSelectedDish(null);
      setSelectedDishIndex({ mealTypeIndex: null, categoryIndex: null, dishIndex: null });
      alert('Dish deleted successfully!');
    } catch (err) {
      console.error('deleteDish error:', err);
      alert('Error deleting dish: ' + err.message);
    } finally {
      setIsSaving(false);
    }
  };

  /* ─────────────── add dish ─────────────── */
  const handleAddDish = async () => {
    if (!newDish.name.trim())   { alert('Please provide a dish name'); return; }
    if (!newDish.image)         { alert('Please upload an image');     return; }
    if (!addCategory.trim())    { alert('Please select or type a category'); return; }
    if (!SIZES.some((s) => sizeEnabled[s])) {
      alert('Please enable at least one size'); return;
    }
    for (const s of SIZES) {
      if (sizeEnabled[s] && (!newDish[s].price || newDish[s].price === '')) {
        alert(`Please provide a price for ${s} size`); return;
      }
    }

    setIsSaving(true);
    try {
      const dishToSave = {
        ...formDishToPayload(
          { ...newDish, category: addCategory.trim(), mealType: selectedMealType },
          cafe._id,
          sizeEnabled
        ),
        createdBy: 'USER',
      };
      delete dishToSave._id;

      const { dish: savedDish } = await callApi('/cafe/adddish', { dish: dishToSave });

      const formShape = flatDishToForm({
        ...savedDish,
        small:  savedDish.sizes?.small,
        medium: savedDish.sizes?.medium,
        large:  savedDish.sizes?.large,
      });

      setMenu((prev) => {
        const next = structuredClone(prev);
        let mti = next.findIndex((m) => m.type === selectedMealType);
        if (mti === -1) { next.push({ type: selectedMealType, categories: [] }); mti = next.length - 1; }
        let ci = next[mti].categories.findIndex((c) => c.name === addCategory.trim());
        if (ci === -1) { next[mti].categories.push({ name: addCategory.trim(), dishes: [] }); ci = next[mti].categories.length - 1; }
        next[mti].categories[ci].dishes.push(formShape);
        return next;
      });

      alert('Dish added successfully!');

      /* Reset add form */
      setNewDish(EMPTY_NEW_DISH);
      setAddCategory('');
      setSizeEnabled(EMPTY_SIZE_ENABLED);
      setImageFiles([]);
      setImageUrls([]);
      setUploadError('');
    } catch (err) {
      console.error('addDish error:', err);
      alert('Error adding dish: ' + err.message);
    } finally {
      setIsSaving(false);
    }
  };

  /* ─────────────── misc ─────────────── */
  const switchMode = (newMode) => {
    setMode(newMode);
    setSelectedDish(null);
    setSelectedDishIndex({ mealTypeIndex: null, categoryIndex: null, dishIndex: null });
    setImageFiles([]);
    setImageUrls([]);
    setUploadError('');
  };

  const toggleSize = (size) => setSizeEnabled((s) => ({ ...s, [size]: !s[size] }));

  /* ═══════════════════════════════════════════════════════
     Render
  ═══════════════════════════════════════════════════════ */
  return (
    <div className="menu-editor-container">

      {/* ── mode switcher ── */}
      <div className="mode-switcher">
        <button className={mode === 'edit' ? 'active' : ''} onClick={() => switchMode('edit')}>Edit Dish</button>
        <button className={mode === 'add'  ? 'active' : ''} onClick={() => switchMode('add')}>Add New Dish</button>
        <button className={mode === 'scan' ? 'active' : ''} onClick={() => switchMode('scan')}>✦ Smart Import</button>
      </div>

      {/* ══════════════════ EDIT MODE ══════════════════ */}
      {mode === 'edit' && (
        <div className="edit-section">
          <h3>Select a Dish to Edit</h3>
          <Select
            options={options}
            onChange={handleDishSelect}
            isClearable
            placeholder="Search and select a dish…"
          />

          {selectedDish && (
            <div className="dish-form">
              <h4>Editing: {selectedDish.name}</h4>

              <div className="form-group">
                <label>Dish Name</label>
                <input
                  type="text"
                  value={selectedDish.name}
                  onChange={(e) => handleInputChange('name', null, e.target.value)}
                />
              </div>

              <div className="form-group">
                <label>Meal Type</label>
                <select
                  value={selectedDish.mealType}
                  onChange={(e) => handleInputChange('mealType', null, e.target.value)}
                >
                  {MEAL_TYPES.map((mt) => (
                    <option key={mt} value={mt}>{mt.charAt(0).toUpperCase() + mt.slice(1)}</option>
                  ))}
                </select>
              </div>

              {/*
                KEY: changing mealType must NOT reset the category the user already
                set. We pass selectedDish.category as the value and update it via
                handleInputChange('category', …). allCategories covers every meal type
                so the dropdown always shows the full universe of categories.
              */}
              <CategoryCombo
                key={`edit-cat-${selectedDish._id}`}
                existingCategories={allCategories}
                value={selectedDish.category}
                onChange={(val) => handleInputChange('category', null, val)}
              />

              <div className="form-group">
                <label>Dish Type</label>
                <select
                  value={selectedDish.type}
                  onChange={(e) => handleInputChange('type', null, e.target.value)}
                >
                  <option value="veg">Veg</option>
                  <option value="nonveg">Non-Veg</option>
                  <option value="egg">Egg</option>
                </select>
              </div>

              <TaxableToggle
                value={selectedDish.isTaxable !== false}
                onChange={(val) => handleInputChange('isTaxable', null, val)}
              />

              <div className="form-group">
                <label>Available</label>
                <select
                  value={String(selectedDish.isAvailable)}
                  onChange={(e) => handleInputChange('isAvailable', null, e.target.value === 'true')}
                >
                  <option value="true">Yes — visible to customers</option>
                  <option value="false">No — hidden from menu</option>
                </select>
              </div>

              {/* Prices grid */}
              <div className="grid-container">
                <div className="grid-header"></div>
                {SIZES.map((size) => (
                  <div key={size} className="grid-header">
                    <strong>{size.charAt(0).toUpperCase() + size.slice(1)}</strong>
                  </div>
                ))}
                <div className="grid-label"><strong>Price (INR)</strong></div>
                {SIZES.map((size) => (
                  <div key={`${size}-price`} className="grid-cell">
                    <input
                      type="number" min="0"
                      value={selectedDish[size]?.price ?? ''}
                      onChange={(e) => handleInputChange('price', size, e.target.value)}
                    />
                  </div>
                ))}
                <div className="grid-label"><strong>Discount (%)</strong></div>
                {SIZES.map((size) => (
                  <div key={`${size}-discount`} className="grid-cell">
                    <input
                      type="number" min="0"
                      value={selectedDish[size]?.discount ?? ''}
                      onChange={(e) => handleInputChange('discount', size, e.target.value)}
                    />
                  </div>
                ))}
              </div>

              <div className="form-group">
  <label>Current Image</label>
  {selectedDish.image ? (
    <div className="current-image-preview">
      <img
        src={selectedDish.image}
        alt={selectedDish.name}
        style={{ width: 120, height: 120, objectFit: 'cover', borderRadius: 8 }}
      />
    </div>
  ) : (
    <p className="no-image-hint">No image set for this dish.</p>
  )}
</div>

              <div className="form-group">
                <label>Upload New Image</label>
                <input
                  type="file"
                  accept="image/png,image/jpeg"
                  onChange={handleFileChange}
                  disabled={isUploading}
                />
                <p className="image-upload-hint">
                  PNG or JPG only. PNGs are automatically converted to optimized JPEGs before upload.
                </p>
                {isUploading && <p className="image-upload-status">Optimizing and uploading…</p>}
                {uploadError && <p className="image-upload-error">{uploadError}</p>}
                {imageUrls.length > 0 && (
                  <div className="image-preview">
                    {imageUrls.map((url, i) => <img key={i} src={url} alt="Preview" />)}
                  </div>
                )}
              </div>

              <div className="button-group">
                <button className="submit-btn" onClick={updateDish} disabled={isSaving}>
                  {isSaving ? 'Saving…' : 'Update Dish'}
                </button>
                <button className="delete-btn" onClick={deleteDish} disabled={isSaving}>
                  Delete Dish
                </button>
              </div>
            </div>
          )}
        </div>
      )}

      {/* ══════════════════ SCAN MODE ══════════════════ */}
      {mode === 'scan' && (
        <MenuScan
          cafeid={cafe?._id}
          onDishAdded={(savedDish) => {
            setMenu((prev) => {
              const next = structuredClone(prev);
              const mealType = savedDish.mealType || 'all';
              let mti = next.findIndex((m) => m.type === mealType);
              if (mti === -1) { next.push({ type: mealType, categories: [] }); mti = next.length - 1; }
              let ci = next[mti].categories.findIndex((c) => c.name === savedDish.category);
              if (ci === -1) { next[mti].categories.push({ name: savedDish.category, dishes: [] }); ci = next[mti].categories.length - 1; }
              next[mti].categories[ci].dishes.push(flatDishToForm(savedDish));
              return next;
            });
          }}
        />
      )}

      {/* ══════════════════ ADD MODE ══════════════════ */}
      {mode === 'add' && (
        <div className="add-section">
          <h3>Add New Dish</h3>

          <div className="form-group">
            <label>Meal Type</label>
            <select value={selectedMealType} onChange={(e) => setSelectedMealType(e.target.value)}>
              {MEAL_TYPES.map((mt) => (
                <option key={mt} value={mt}>{mt.charAt(0).toUpperCase() + mt.slice(1)}</option>
              ))}
            </select>
          </div>

          {/*
            allCategories covers every category in the menu regardless of meal type,
            so a "Starters" category defined under lunch also appears when adding a
            dinner dish — matching real-world restaurant workflows.
          */}
          <CategoryCombo
            existingCategories={allCategories}
            value={addCategory}
            onChange={setAddCategory}
          />

          <div className="form-group">
            <label>Dish Name</label>
            <input
              type="text"
              value={newDish.name}
              onChange={(e) => handleInputChange('name', null, e.target.value)}
              placeholder="Enter dish name"
            />
          </div>

          <div className="form-group">
            <label>Dish Type</label>
            <select
              value={newDish.type}
              onChange={(e) => handleInputChange('type', null, e.target.value)}
            >
              <option value="veg">Veg</option>
              <option value="nonveg">Non-Veg</option>
              <option value="egg">Egg</option>
            </select>
          </div>

          <TaxableToggle
            value={newDish.isTaxable}
            onChange={(val) => handleInputChange('isTaxable', null, val)}
          />

          <div className="sizes-section">
            <h4>Available Sizes</h4>
            {SIZES.map((size) => (
              <div key={size} className="size-group">
                <div className="size-header">
                  <input
                    type="checkbox"
                    id={`size-${size}`}
                    checked={sizeEnabled[size]}
                    onChange={() => toggleSize(size)}
                  />
                  <label htmlFor={`size-${size}`}>
                    {size.charAt(0).toUpperCase() + size.slice(1)}
                  </label>
                </div>
                {sizeEnabled[size] && (
                  <div className="size-inputs">
                    <div className="input-wrapper">
                      <label>Price (INR)</label>
                      <input
                        type="number" min="0"
                        value={newDish[size].price}
                        onChange={(e) => handleInputChange('price', size, e.target.value)}
                        placeholder="0"
                      />
                    </div>
                    <div className="input-wrapper">
                      <label>Discount (%)</label>
                      <input
                        type="number" min="0"
                        value={newDish[size].discount}
                        onChange={(e) => handleInputChange('discount', size, e.target.value)}
                        placeholder="0"
                      />
                    </div>
                  </div>
                )}
              </div>
            ))}
          </div>

          <div className="form-group">
            <label>Upload Image</label>
            <input
              type="file"
              accept="image/png,image/jpeg"
              onChange={handleFileChange}
              disabled={isUploading}
            />
            <p className="image-upload-hint">
              PNG or JPG only. PNGs are automatically converted to optimized JPEGs before upload.
            </p>
            {isUploading && <p className="image-upload-status">Optimizing and uploading…</p>}
            {uploadError && <p className="image-upload-error">{uploadError}</p>}
            {imageUrls.length > 0 && (
              <div className="image-preview">
                {imageUrls.map((url, i) => <img key={i} src={url} alt="Preview" />)}
              </div>
            )}
          </div>

          <button className="submit-btn" onClick={handleAddDish} disabled={isSaving || isUploading}>
            {isSaving ? 'Adding…' : 'Add Dish'}
          </button>
        </div>
      )}
    </div>
  );
};

export default MenuEditor;